import { supabase } from './supabase';
import { captureFrontendException, sanitizeSensitiveData } from './sentry';

type LogLevel = 'info' | 'warn' | 'error' | 'critical' | 'audit';

interface LogEntry {
  level: LogLevel;
  category: string;
  message: string;
  metadata?: Record<string, any>;
  userId?: string;
}

/**
 * Production-ready Logger
 * - Development: Logs to console
 * - Production: Logs to console AND Supabase (for warn/error/critical)
 * - Observability: Forwards error/critical events seamlessly to Sentry
 */
class Logger {
  private isDevelopment = (() => {
    const env = (typeof import.meta !== 'undefined' && (import.meta as any)?.env) || (typeof process !== 'undefined' && process.env) || {};
    return env.DEV || env.NODE_ENV !== 'production';
  })();

  async log({ level, category, message, metadata = {}, userId }: LogEntry) {
    // Aggressively sanitize logged elements to protect user PII
    const safeMeta = metadata || {};
    const sanitizedMeta = sanitizeSensitiveData(safeMeta);
    const consoleMethod = level === 'error' || level === 'critical' ? 'error' : level === 'warn' ? 'warn' : 'log';
    
    if (this.isDevelopment) {
      console[consoleMethod](`[${(category || 'GENERAL').toUpperCase()}] ${message}`, sanitizedMeta);
    }

    // Capture errors and critical failures in Sentry dynamically
    if (level === 'error' || level === 'critical') {
      const errorObject = (safeMeta.error instanceof Error)
        ? safeMeta.error 
        : new Error(message || 'Unknown error');

      captureFrontendException(errorObject, category || 'general', {
        flow_type: category || 'general',
        level,
        userId: userId || 'anonymous'
      }, sanitizedMeta);
    }

    // 2. Persist to Supabase if important enough or in production
    // We log errors, critical, and audit events to the DB when Supabase is available
    if (level === 'error' || level === 'critical' || level === 'audit' || !this.isDevelopment) {
      // Prevent circular loops on config errors or logging system warnings
      if (category === 'CONFIG' || category === 'LOG_FAILURE') {
        return;
      }

      try {
        if (supabase && typeof supabase.from === 'function') {
          const table = supabase.from('system_logs');
          if (table && typeof table.insert === 'function') {
            const safeUrl = typeof window !== 'undefined' && window.location ? window.location.href : '';
            const safeUserAgent = typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '';

            const { error } = await table.insert({
              level,
              category,
              message,
              metadata: {
                ...sanitizedMeta,
                url: safeUrl,
                userAgent: safeUserAgent
              },
              user_id: userId
            });

            if (error && this.isDevelopment) {
              console.warn('[Logger] Failed to persist log entry:', error.message);
            }
          }
        }
      } catch (err) {
        // Fallback for logging failure - non-blocking
        if (this.isDevelopment) {
          console.warn('[Logger] Could not persist log to database:', err);
        }
      }
    }
  }

  info(category: string, message: string, metadata?: Record<string, any>) {
    this.log({ level: 'info', category, message, metadata });
  }

  warn(category: string, message: string, metadata?: Record<string, any>) {
    this.log({ level: 'warn', category, message, metadata });
  }

  error(category: string, message: string, metadata?: Record<string, any>, userId?: string) {
    this.log({ level: 'error', category, message, metadata, userId });
  }

  critical(category: string, message: string, metadata?: Record<string, any>, userId?: string) {
    this.log({ level: 'critical', category, message, metadata, userId });
  }

  audit(category: string, message: string, metadata?: Record<string, any>, userId?: string) {
    this.log({ level: 'audit', category, message, metadata, userId });
  }
}

export const logger = new Logger();
